/**
 * State that must be the SAME object however many times this module graph is
 * evaluated.
 *
 * The agent's seams to React are plain module-level objects: the router the
 * navigation module writes (`navigationRefs`) and the provider runtime the
 * executor reports through. A module-level `const` is one object per module
 * INSTANCE, and a module can be instantiated more than once — webpack's hot
 * reload does exactly that when a non-component module is swapped under a live
 * page, and any bundle that ends up with two copies of a file does too.
 *
 * When that happens the halves silently stop seeing each other: the bridge
 * writes the router into one `navigationRefs` while the executor reads the
 * other, finds it empty, and answers every navigation with
 * `[api_result] navigate failed: the app router is not ready` — on a page that
 * is plainly mounted and working. It reads exactly like an agent bug and it is
 * not one; it survives until a hard reload.
 *
 * `Symbol.for` is the global symbol registry, so every copy of THIS file
 * resolves the same key, and the store hangs off `globalThis`, which no
 * duplication can fork. The first caller creates the value and everyone else —
 * including a second instance of the calling module — gets that one.
 *
 * Dev-server hygiene, not a production mechanism: a production bundle
 * evaluates each module once and `create` runs once either way.
 */
const REGISTRY = Symbol.for('ki.agent.singletons');

/**
 * The one value registered under `key`, creating it on first use.
 *
 * `create` must return an object, because the value is shared by identity: a
 * caller that needs to REPLACE what it holds keeps a box and reassigns a field
 * on it (`box.current = …`), since a rebound `let` would only ever move this
 * module instance's own binding.
 */
export function agentSingleton(key, create) {
  const host = globalThis;
  const registry = host[REGISTRY] || (host[REGISTRY] = new Map());
  if (!registry.has(key)) registry.set(key, create());
  return registry.get(key);
}

/** The keys registered so far — for the check scripts and devtools only. */
export function agentSingletonKeys() {
  const registry = globalThis[REGISTRY];
  return registry ? [...registry.keys()] : [];
}
